import {
  ColDef,
  GridReadyEvent,
  ICellRendererParams,
  IServerSideDatasource,
} from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import { Box, Stack, Typography } from '@atgfr8/atg-ui-tailwind';
import { BasicModal, ToastType } from '@components';
import { Button } from '@components/Button';
import {
  columnsToolPanel,
  filtersToolPanel,
} from '@components/Grid/components/constants';
import { alternateRowStyle } from '@components/Grid/utils';
import {
  getDormantPoolListApi,
  markCustomerNotInterestedApi,
  takeCustomerApi,
} from '@store/dormantPool';
import { dormatPoolStore, setDormantPoolStore } from '@store/dormantPool/store';
import { dateComparator, dateFormatter } from '@store/loadboard/utils';
import { openModal } from '@store/modals';
import { DormantPoolListType } from '@typeDefinitions/dormantPoolTypes';
import { handleToast } from '@utils/utils';
import { atgLogo } from '@views/ltl';
import { Accessor, createSignal, Setter } from 'solid-js';

import { transformFilters } from '../constants';
import { ClaimedCustomers } from './ClaimedCustomers';
import { CustomTextFloatingFilter } from './CustomFloatingFilter';

const commonFilterParams = {
  suppressAndOrCondition: true,
  filterOptions: ['contains'],
};

type ListViewProps = {
  viewMode: Accessor<'List' | 'Grid'>;
  gridRef: Accessor<AgGridSolidRef | undefined>;
  setGridRef: Setter<AgGridSolidRef | undefined>;
  hideNotInterested: Accessor<boolean>;
  gridPayload?: Accessor<Record<string, unknown>>;
  setGridPayload: Setter<Record<string, unknown>>;
};

export const ListView = (props: ListViewProps) => {
  const [takeCustomer, setTakeCustomer] =
    createSignal<DormantPoolListType | null>(null);

  const handleTake = async (customer: DormantPoolListType) => {
    props.gridRef()?.api.showLoadingOverlay();
    const response = await takeCustomerApi(customer.id);
    if (Boolean(response)) {
      const claimedCustomer = { ...customer, dateClaimed: new Date() };
      const updatedClaimedList = [...dormatPoolStore.claimed, claimedCustomer];
      setDormantPoolStore('claimed', updatedClaimedList);
      props.gridRef()?.api.refreshServerSide({ purge: true });
      handleToast(ToastType.Success, 'Customer claimed.');
    }
    props.gridRef()?.api.hideOverlay();
  };

  const toggleCustomerInterest = async (data: DormantPoolListType) => {
    props.gridRef()?.api.showLoadingOverlay();
    const status = data.isNotInterested;
    const response = await markCustomerNotInterestedApi(data.id, status);
    if (response ?? false) {
      props.gridRef()?.api.refreshServerSide({ purge: true });
      handleToast(
        ToastType.Success,
        `Customer marked as ${!status ? 'Not Interested' : 'Interested'}.`,
      );
    }
    props.gridRef()?.api.hideOverlay();
  };

  const columns: ColDef[] = [
    {
      field: 'customerName',
      headerName: 'Customer',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomTextFloatingFilter,
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      filterParams: commonFilterParams,
      flex: 2,
      sort: 'desc',
      cellClass: 'remove-auto-line-height',
    },
    {
      field: 'industry',
      headerName: 'Industry',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomTextFloatingFilter,
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      filterParams: commonFilterParams,
      flex: 2,
      cellClass: 'remove-auto-line-height',
    },
    {
      field: 'lastDropoffDate',
      headerName: 'Last Load Date',
      filter: 'agDateColumnFilter',
      valueFormatter: dateFormatter,
      floatingFilter: true,
      filterParams: {
        comparator: dateComparator,
        filterOptions: ['equals', 'greaterThan', 'lessThan'],
        suppressAndOrCondition: true,
        minValidYear: 1900,
        maxValidYear: new Date().getFullYear() + 1,
      },
      flex: 1.25,
    },
    {
      field: 'loads',
      headerName: 'Loads Moved',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomTextFloatingFilter,
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      filterParams: commonFilterParams,
      flex: 1,
    },
    {
      field: 'equipments',
      headerName: 'Equipment',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomTextFloatingFilter,
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      filterParams: commonFilterParams,
      flex: 2,
      cellClass: 'remove-auto-line-height',
    },
    {
      field: 'originStates',
      headerName: 'Pickup State(s)',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomTextFloatingFilter,
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      filterParams: commonFilterParams,
      flex: 1,
      cellClass: 'remove-auto-line-height',
    },
    {
      field: 'destinationStates',
      headerName: 'Dropoff State(s)',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomTextFloatingFilter,
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      filterParams: commonFilterParams,
      flex: 1,
      cellClass: 'remove-auto-line-height',
    },
    {
      field: 'isNotInterested',
      headerName: 'Action',
      flex: 2,
      cellRenderer: (params: ICellRendererParams) => {
        const isNotInterested = Boolean(params.value);
        return (
          <Box
            style={{
              display: 'flex',
              'flex-wrap': 'wrap',
            }}
            gap={1}
          >
            <Button
              label="Take"
              color="success"
              size="sm"
              sx={{
                'white-space': 'nowrap',
                'font-size': 'clamp(10px, 1.2vw, 12px)',
              }}
              onClick={() => {
                if (dormatPoolStore.claimed.length >= 5) {
                  handleToast(
                    ToastType.Error,
                    'Cannot select more customers. Please remove existing customers and then try.',
                  );
                } else {
                  setTakeCustomer(params.data);
                  openModal('dormantpool_take_modal');
                }
              }}
              dataTestId="dormant-pool-take-button"
            />
            <Button
              label={isNotInterested ? 'Interested' : 'Not Interested'}
              color={isNotInterested ? 'primary' : 'error'}
              size="sm"
              sx={{
                'white-space': 'nowrap',
                'font-size': 'clamp(10px, 1.2vw, 12px)',
              }}
              onClick={() =>
                toggleCustomerInterest(params.data as DormantPoolListType)
              }
              dataTestId="dormant-pool-toggle-interest-button"
            />
          </Box>
        );
      },
    },
  ];

  const onGridReady = (params: GridReadyEvent) => {
    props.setGridRef(params);
    const dataSource = {
      getRows: async (params: {
        api: {
          paginationGetCurrentPage: () => number;
        };
        request: {
          filterModel: {
            [key: string]: {
              filter?: string;
              dateFrom?: string | null;
              dateTo?: null;
              filterType: 'date' | 'text' | 'set';
              type: string;
              values: string[];
            };
          };
          sortModel: {
            colId: string;
            sort: string;
          }[];
        };
        success: (data: {
          rowData: DormantPoolListType[];
          rowCount: number;
        }) => void;
        fail: () => void;
      }) => {
        let orderBy = '';
        let sortBy = '';
        if (params.request.sortModel.length > 0) {
          orderBy = params.request.sortModel[0].colId;
          sortBy = params.request.sortModel[0].sort;
        }
        const filteredObject = {
          filters: [...transformFilters(params.request.filterModel)],
          sort: {
            field: orderBy || 'customerName',
            direction: sortBy || 'asc',
          },
          pageNumber: params.api.paginationGetCurrentPage() + 1,
          pageSize: 500,
        };
        props.setGridPayload(filteredObject);
        try {
          props.gridRef()?.api.showLoadingOverlay();
          const response = await getDormantPoolListApi(
            filteredObject,
            props.hideNotInterested(),
          );
          params.success({
            rowData: response?.data || [],
            rowCount: response?.totalCount ?? 0,
          });
        } catch (error) {
          params.fail();
        } finally {
          props.gridRef()?.api.hideOverlay();
        }
      },
    };
    params.api.setGridOption(
      'serverSideDatasource',
      dataSource as unknown as IServerSideDatasource,
    );
  };

  return (
    <>
      <Stack class="h-full gap-1">
        <ClaimedCustomers viewMode={props.viewMode} gridRef={props.gridRef} />
        <Box class="ag-theme-alpine flex-1! min-h-[400px]">
          <AgGridSolid
            ref={props.gridRef}
            overlayNoRowsTemplate="No data available"
            overlayLoadingTemplate={atgLogo}
            columnDefs={columns}
            getRowStyle={alternateRowStyle}
            rowHeight={40}
            gridOptions={{
              defaultColDef: {
                filter: true,
                wrapText: true,
                autoHeight: true,
                suppressMovable: true,
              },
              pagination: true,
              suppressPaginationPanel: false,
              maintainColumnOrder: true,
              paginationPageSizeSelector: false,
              sideBar: {
                toolPanels: [columnsToolPanel, filtersToolPanel],
              },
              rowModelType: 'serverSide',
              paginationPageSize: 500,
              cacheBlockSize: 500,
              onGridReady: onGridReady,
              getContextMenuItems: (params) => {
                const defaultItems = params.defaultItems || [];
                return defaultItems.filter(
                  (item) =>
                    item !== 'export' &&
                    item !== 'csvExport' &&
                    item !== 'excelExport',
                );
              },
            }}
          />
        </Box>
      </Stack>
      <BasicModal
        id="dormantpool_take_modal"
        title="Confirmation"
        onSubmitText="Ok"
        onSubmit={async () => {
          const selectedCustomer = takeCustomer();
          if (selectedCustomer) {
            await handleTake(selectedCustomer);
          }
        }}
        footer
      >
        <Typography variant="body1" class="pl-2">
          {`Are you sure you want to take customer ${
            takeCustomer()?.customerName
          }?`}
        </Typography>
      </BasicModal>
    </>
  );
};
